import { apiFetch } from "@/lib/api";

/** Standard Django envelope. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** The three section keys the backend groups notification events under. */
export type NotificationSectionKey =
  | "client_portal"
  | "irs_advance_notice"
  | "other";

/** A single notification event the firm can toggle alerts/email for. */
export interface NotificationEvent {
  key: string;
  label: string;
  tooltip: string;
  alertEnabled: boolean;
  emailEnabled: boolean;
  /** Comma-separated recipient emails (as stored/entered). */
  emailRecipients: string;
}

export type NotificationSections = Record<
  NotificationSectionKey,
  NotificationEvent[]
>;

/** One row of the PATCH payload. */
export interface NotificationPreference {
  eventKey: string;
  alertEnabled: boolean;
  emailEnabled: boolean;
  emailRecipients: string;
}

interface ApiNotificationEvent {
  key: string;
  label: string;
  tooltip: string;
  alert_enabled: boolean;
  email_enabled: boolean;
  email_recipients: string;
}

type ApiSections = Record<NotificationSectionKey, ApiNotificationEvent[]>;

function toEvent(e: ApiNotificationEvent): NotificationEvent {
  return {
    key: e.key,
    label: e.label,
    tooltip: e.tooltip,
    alertEnabled: e.alert_enabled,
    emailEnabled: e.email_enabled,
    emailRecipients: e.email_recipients,
  };
}

function toSections(s: ApiSections): NotificationSections {
  return {
    client_portal: (s.client_portal ?? []).map(toEvent),
    irs_advance_notice: (s.irs_advance_notice ?? []).map(toEvent),
    other: (s.other ?? []).map(toEvent),
  };
}

export async function getNotifications(): Promise<NotificationSections> {
  const { data } = await apiFetch<Envelope<{ sections: ApiSections }>>(
    "/firm/notifications",
  );
  return toSections(data.sections);
}

export async function saveNotifications(
  prefs: NotificationPreference[],
): Promise<NotificationSections> {
  const { data } = await apiFetch<Envelope<{ sections: ApiSections }>>(
    "/firm/notifications",
    {
      method: "PATCH",
      json: {
        preferences: prefs.map((p) => ({
          event_key: p.eventKey,
          alert_enabled: p.alertEnabled,
          email_enabled: p.emailEnabled,
          email_recipients: p.emailRecipients,
        })),
      },
    },
  );
  return toSections(data.sections);
}

export async function restoreNotifications(): Promise<NotificationSections> {
  const { data } = await apiFetch<Envelope<{ sections: ApiSections }>>(
    "/firm/notifications/restore",
    { method: "POST" },
  );
  return toSections(data.sections);
}
